import { ConfirmDialog } from '@/components/ui/custom/ConfirmDialog';
import { EditableTitle } from '@/components/ui/custom/EditableTitle';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useArtifactStore } from '@/store/artifactStore';
import { useUIStore } from '@/store/uiStore';
import { Artifact } from '@/types/artifact';
import { formatDate } from '@/utils/formatters';
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import ArtifactPreview from './ArtifactPreview';

// Lazy load heavy components
const CodeEditor = React.lazy(() => import('./CodeEditor'));

interface ArtifactEditorProps {
  artifact: Artifact;
  className?: string;
}

interface LoadingFallbackProps {
  height?: string;
}

const LoadingFallback: React.FC<LoadingFallbackProps> = ({ height = '400px' }) => (
  <div className={`flex items-center justify-center bg-muted/50 rounded-lg`} style={{ height }}>
    <div className="flex flex-col items-center space-y-2">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      <p className="text-sm text-muted-foreground">Loading editor...</p>
    </div>
  </div>
);

export const ArtifactEditor: React.FC<ArtifactEditorProps> = ({
  artifact,
  className,
}) => {
  const { updateArtifact, isSaving } = useArtifactStore();
  const {
    editorSplitWidth,
    showEditor,
    setEditorSplitWidth,
    toggleEditor,
  } = useUIStore();

  const [htmlContent, setHtmlContent] = useState(artifact.htmlContent);
  const [isDirty, setIsDirty] = useState(false);

  const [isResizing, setIsResizing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(artifact.updatedAt);
  const [, setUpdateTrigger] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout>();

  // Check for mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Update relative time display every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setUpdateTrigger(prev => prev + 1);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Clean up auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Mouse move handler for resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      let newSplit;

      if (isMobile) {
        // Vertical split for mobile
        const percentage = ((e.clientY - rect.top) / rect.height) * 100;
        newSplit = Math.max(30, Math.min(70, percentage));
      } else {
        // Horizontal split for desktop
        const percentage = ((e.clientX - rect.left) / rect.width) * 100;
        newSplit = Math.max(30, Math.min(70, percentage));
      }

      setEditorSplitWidth(newSplit);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, isMobile, setEditorSplitWidth]);

  const handleContentChange = useCallback((value: string) => {
    const newContent = value || '';
    setHtmlContent(newContent);
    setIsDirty(true);
  }, []);

  const htmlContentRef = useRef(htmlContent);
  
  useEffect(() => {
    htmlContentRef.current = htmlContent;
  }, [htmlContent]);

  const handleSave = useCallback(async () => {
    const currentContent = htmlContentRef.current;
    try {
      await updateArtifact(artifact.id, { htmlContent: currentContent });
      setIsDirty(false);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save artifact:', error);
    }
  }, [artifact.id, updateArtifact]);

  // Unsaved changes handling
  const {
    showConfirmDialog,
    confirmNavigation,
    saveAndNavigate,
  } = useUnsavedChanges({
    hasUnsavedChanges: isDirty,
    onSave: handleSave,
  });

  const handlePreviewError = useCallback((error: string) => {
    console.error('Preview error:', error);
  }, []);

  const handleToggleEditor = useCallback(() => {
    toggleEditor();
  }, [toggleEditor]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  return (
    <div className={`flex flex-col h-full bg-background ${className || ''}`}>
      {/* Header */}
      <div className="flex items-center gap-4 p-4 border-b border-border bg-card/50">
        <div className="flex items-center gap-2">
          <EditableTitle
            value={artifact.title}
            onChange={async (newTitle: string) => {
              await updateArtifact(artifact.id, { title: newTitle });
            }}
            className="text-lg font-semibold"
          />
          {(isDirty || isSaving) && (
            <span className="text-orange-500 text-xl leading-none w-4 inline-block text-center">
              {isSaving ? (
                <span className="animate-pulse">...</span>
              ) : (
                <span className="relative flex size-2">
                  <span className="relative inline-flex size-2 rounded-full bg-orange-500"></span>
                </span>
              )}
            </span>
          )}
        </div>

        {/* Last saved indicator */}
        {lastSaved && (
          <div className="text-sm text-muted-foreground">
            {formatDate(lastSaved)}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex min-h-0" ref={containerRef}>
        <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} w-full h-full`}>
          {showEditor && (
            <>
              <div
                className="bg-muted/30 border-r border-border flex flex-col"
                style={{
                  width: !isMobile ? `${editorSplitWidth}%` : '100%',
                  height: isMobile ? `${editorSplitWidth}%` : 'auto'
                }}
              >
                <Suspense fallback={<LoadingFallback />}>
                  <CodeEditor
                    value={htmlContent}
                    onChange={handleContentChange}
                    onSave={handleSave}
                    language="html"
                    theme="vs-dark"
                    autoSave={false}
                    className="flex-1"
                  />
                </Suspense>
              </div>

              {/* Resize Handle */}
              <div
                className={`
                  ${isMobile ? 'h-2 w-full cursor-row-resize' : 'w-2 h-full cursor-col-resize'}
                  bg-border hover:bg-primary/20 transition-colors flex-shrink-0 group
                `}
                onMouseDown={handleResizeStart}
              >
                <div className={`
                  ${isMobile ? 'w-8 h-full mx-auto' : 'h-8 w-full my-auto'}
                  bg-muted-foreground/20 group-hover:bg-primary/40 transition-colors
                  ${isMobile ? 'mt-0.5' : 'ml-0.5'}
                `} />
              </div>
            </>
          )}

          {/* Preview */}
          <div
            className="flex flex-col bg-background"
            style={{
              width: !isMobile ? (showEditor ? `${100 - editorSplitWidth}%` : '100%') : '100%',
              height: isMobile ? (showEditor ? `${100 - editorSplitWidth}%` : '100%') : 'auto'
            }}
          >
            <ArtifactPreview
              htmlContent={htmlContent}
              onError={handlePreviewError}
              onToggleEditor={handleToggleEditor}
              showEditor={showEditor}
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {/* Unsaved changes confirmation dialog */}
      <ConfirmDialog
        open={showConfirmDialog}
        title="Unsaved Changes"
        description="You have unsaved changes that will be lost if you leave this page. Do you want to save your changes before leaving?"
        confirmText="Save & Leave"
        cancelText="Don't Save"
        onConfirm={saveAndNavigate}
        onCancel={confirmNavigation}
      />
    </div>
  );
}; 